import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/button";
import { ButtonGroup } from "~/components/ui/button-group";
import { Link } from "~/components/ui/link";
import { HighlightsAccordion } from "~/components/products/HighlightsAccordion";
import { FeaturesSection } from "~/components/products/pdp/FeaturesSection";
import { ProductPromoSection } from "~/components/products/pdp/ProductPromoSection";
import { VideoFeatureSection } from "~/components/products/pdp/VideoFeatureSection";
import { SpecsSection } from "~/components/products/pdp/SpecsSection";
import { FleetContractBanner } from "~/components/products/pdp/FleetContractBanner";
import { ObdCompatibilityCheckerSection } from "~/components/products/pdp/ObdCompatibilityCheckerSection";
import { FaqSection } from "~/components/products/pdp/FaqSection";
import { FleetLastChance } from "~/components/fleet/FleetLastChance";
import { FleetPricingSection, type FleetPrices } from "~/components/products/pdp/FleetPricingSection";
import type { FleetProductPageContent } from "~/data/products/types";

interface FleetProductPdpTemplateProps {
  content: FleetProductPageContent;
  prices: FleetPrices;
}

// 16px bold, 52px tall, sharing the row equally — same CTA sizing as the /fleet page.
const ctaClassName = "h-[52px] flex-1 px-4 text-base leading-5 tracking-[0.16px]";

// Fleet counterpart of ProductPdpTemplate. Header and footer come from site-layout via
// the route's handle; this only scopes the body to the dark fleet tokens.
export function FleetProductPdpTemplate({ content, prices }: FleetProductPdpTemplateProps) {
  const { t } = useTranslation("products");

  return (
    <>
      <title>{t(content.metaTitle)}</title>
      <meta name="description" content={t(content.metaDescription)} />
      <div className="fleet bg-background text-text-body">
        <FleetPricingSection name={content.name} content={content.hero} prices={prices} />
        <HighlightsAccordion
          content={content.highlights}
          ctas={
            <ButtonGroup layout="row" className="w-full">
              <Button asChild variant="secondary-alt" className={ctaClassName}>
                <Link data-sid="fleet-pdp-highlights-contact-sales" to="/contact">
                  {t("drive_fleet.highlights.ctas.contactSales")}
                </Link>
              </Button>
              <Button asChild variant="primary-alt" className={ctaClassName}>
                <Link data-sid="fleet-pdp-highlights-build-quote" to="/fleet/quote">
                  {t("drive_fleet.highlights.ctas.buildQuote")}
                </Link>
              </Button>
            </ButtonGroup>
          }
        />
        <FeaturesSection content={content.features} />
        <ProductPromoSection content={content.promo} />
        <VideoFeatureSection content={content.video} />
        <SpecsSection content={content.specs} />
        <FleetContractBanner content={content.contractBanner} />
        {/* The drawer still offers add-to-cart; a fleet CTA (Build Quote, or none) is phase 2. */}
        <ObdCompatibilityCheckerSection />
        <FaqSection content={content.faq} defaultOpenFirst />
        {/* Same section, copy and photo as /fleet's closing CTA. */}
        <FleetLastChance />
      </div>
    </>
  );
}
